import { useEffect, useRef, useState } from "react";
import { Pause, Play, X } from "lucide-react";
import { LeaderboardResults } from "@/components/LeaderboardResults";

export const LeaderboardDisplay = ({ data, groups, error, onExit, onRetry }) => {
  const viewport = useRef(null);
  const content = useRef(null);
  const exitButton = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [paused, setPaused] = useState(false);
  const hasRows = groups.some((group) => group.entries.length);
  useEffect(() => {
    exitButton.current?.focus();
    const escape = (event) => { if (event.key === "Escape") onExit(); };
    document.addEventListener("keydown", escape);
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", escape); document.body.style.overflow = oldOverflow; };
  }, [onExit]);
  useEffect(() => {
    const container = viewport.current;
    const original = content.current;
    if (!container || !original) return;
    const measure = () => { const needed = original.offsetHeight > container.clientHeight + 1; setOverflow(needed); if (!needed) container.scrollTop = 0; };
    const observer = new ResizeObserver(measure);
    observer.observe(container); observer.observe(original); measure();
    return () => { observer.disconnect(); };
  }, [hasRows]);
  useEffect(() => {
    if (!overflow || paused) return;
    let frame, previous, offset = viewport.current?.scrollTop || 0;
    const tick = (now) => {
      if (previous != null && viewport.current && content.current) {
        offset += Math.min(now - previous, 64) * 0.026;
        if (content.current.offsetHeight > 0) offset %= content.current.offsetHeight;
        viewport.current.scrollTop = offset;
      }
      previous = now; frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(frame); };
  }, [overflow, paused]);
  return <section className="fixed inset-0 z-[100] bg-[#FDFFFC] flex flex-col p-3 sm:p-8" role="region" aria-label="Leaderboard Display Mode" data-testid="leaderboard-display">
    <header className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,1fr)] items-start gap-3 pb-4 border-b border-slate-200 shrink-0" data-testid="display-header">
      <div className="min-w-0 text-center lg:col-start-2" data-testid="display-title-group"><p className="text-xs uppercase tracking-widest font-bold text-[#B36906]" data-testid="display-brand">Naptown Cubers · Leaderboard</p><h1 className="mt-1 font-display font-black text-xl sm:text-4xl break-words" data-testid="display-event-title">{data?.event.title || "Leaderboard"}</h1></div>
      <div className="flex flex-wrap justify-end gap-2 shrink-0 justify-self-end lg:col-start-3" data-testid="display-controls">
        {overflow && <button type="button" onClick={() => setPaused((value) => !value)} data-testid="display-pause" className="rounded-full px-4 py-2 border border-slate-300 font-semibold text-sm inline-flex gap-2 items-center">{paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}{paused ? "Resume" : "Pause"}</button>}
        <button ref={exitButton} type="button" onClick={onExit} data-testid="display-exit" className="rounded-full px-4 py-2 bg-[#1E1E24] text-white font-semibold text-sm inline-flex gap-2 items-center"><X className="w-4 h-4" /> Exit</button>
      </div>
    </header>
    {error && <p role="alert" data-testid="display-refresh-error" className="py-3 text-sm text-red-700">{error}<button onClick={onRetry} data-testid="display-retry" className="ml-3 underline">Retry</button></p>}
    <div ref={viewport} data-testid="display-scroll-viewport" data-scrolling={overflow && !paused ? "true" : "false"} className={`flex-1 min-h-0 mt-4 ${paused ? "overflow-y-auto" : "overflow-hidden"}`}>
      <div ref={content} data-testid="display-original-content">{hasRows ? <LeaderboardResults groups={groups} presentation prefix="display" /> : <p className="p-8 text-center text-[#7A7D8C]" data-testid="display-empty">{data ? "No competitors to display yet." : "Loading leaderboard…"}</p>}</div>
      {overflow && <div aria-hidden="true" data-testid="display-loop-copy"><LeaderboardResults groups={groups} presentation prefix="display-copy" /></div>}
    </div>
    <footer className="pt-3 text-xs text-[#7A7D8C] flex justify-between gap-3" data-testid="display-status"><span>{error ? "Updates paused · Last available results shown" : "Refreshes every 15 seconds"}</span><span>{overflow ? paused ? "Scrolling paused" : "Continuous scrolling" : "All results fit on screen"} · Esc to exit</span></footer>
  </section>;
};
